/**
 * Журнал действий проекта.
 *
 * Отвечает на вопрос «что изменилось», но не служит мерой прогресса
 * (документ «Итоговое описание», §19). Прогресс измеряется выполненными
 * требованиями этапа, а не количеством записей в ленте.
 *
 * Записи группируются по дням: лента из сотни строк без разделителей
 * нечитаема.
 */

import { ActivityIcon, Loader2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  ACTIVITY_ACTIONS,
  ACTIVITY_ACTION_LABELS_RU,
  DISPLAY_TIMEZONE,
  type ActivityAction,
  type ActivityDto,
} from '@project-os/contracts';
import { useActivity, type ActivityQuery } from '@/api/collaboration';
import { useProjectMembers } from '@/api/members';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/common/states';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserAvatar } from '@/components/common/user-avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDateTime } from '@/lib/format';
import { ItemTypeIcon } from '../items/item-icon';
import { useProjectContext } from './use-project-context';

const ALL = '__all__';

/** Ключ дня в часовом поясе отображения. */
function dayKey(iso: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: DISPLAY_TIMEZONE,
  }).format(new Date(iso));
}

function timeOfDay(iso: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: DISPLAY_TIMEZONE,
  }).format(new Date(iso));
}

export function ActivityPage() {
  const project = useProjectContext();
  const { data: members = [] } = useProjectMembers(project.id);

  const [userId, setUserId] = useState<string>(ALL);
  const [action, setAction] = useState<string>(ALL);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const query = useMemo<ActivityQuery>(
    () => ({
      ...(userId === ALL ? {} : { userId }),
      ...(action === ALL ? {} : { action: [action as ActivityAction] }),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    }),
    [userId, action, from, to],
  );

  const activity = useActivity(project.id, query);
  const entries = activity.data?.pages.flatMap((page) => page.data) ?? [];

  // Действия, встречающиеся в проекте, показывать нет откуда — список берётся
  // из перечисления, но отсортирован по алфавиту подписей для удобства.
  const actionOptions = useMemo(
    () =>
      [...ACTIVITY_ACTIONS]
        .map((value) => ({ value, label: ACTIVITY_ACTION_LABELS_RU[value] }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ru')),
    [],
  );

  const hasFilters = userId !== ALL || action !== ALL || from !== '' || to !== '';

  /** Сброс нужен и в панели, и в пустом состоянии — поэтому одна функция. */
  const resetFilters = () => {
    setUserId(ALL);
    setAction(ALL);
    setFrom('');
    setTo('');
  };

  const grouped = useMemo(() => {
    const groups = new Map<string, ActivityDto[]>();
    for (const entry of entries) {
      const key = dayKey(entry.createdAt);
      const list = groups.get(key);
      if (list) list.push(entry);
      else groups.set(key, [entry]);
    }
    return [...groups.entries()];
  }, [entries]);

  return (
    <>
      <PageHeader
        title="История"
        description="Кто и что менял в проекте. Журнал только пополняется — записи не изменяются и не удаляются."
      />

      <div className="flex flex-wrap items-end gap-3 border-b px-6 py-3">
        <div className="space-y-1.5">
          <Label htmlFor="activity-user" className="text-xs">
            Участник
          </Label>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger id="activity-user" size="sm" className="w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Все участники</SelectItem>
              {members.map((member) => (
                <SelectItem key={member.user.id} value={member.user.id}>
                  {member.user.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="activity-action" className="text-xs">
            Действие
          </Label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger id="activity-action" size="sm" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Любое действие</SelectItem>
              {actionOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="activity-from" className="text-xs">
            С даты
          </Label>
          <Input
            id="activity-from"
            type="date"
            className="h-8 w-40"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="activity-to" className="text-xs">
            По дату
          </Label>
          <Input
            id="activity-to"
            type="date"
            className="h-8 w-40"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>

        {hasFilters ? (
          <Button variant="ghost" size="sm" className="h-8" onClick={resetFilters}>
            Сбросить
          </Button>
        ) : null}
      </div>

      <PageBody>
        {activity.isPending ? (
          <TableSkeleton rows={8} columns={3} />
        ) : activity.isError ? (
          <ErrorState error={activity.error} onRetry={() => void activity.refetch()} />
        ) : entries.length === 0 ? (
          <EmptyState
            icon={<ActivityIcon />}
            hint={
              hasFilters
                ? null
                : 'Журнал только пополняется: записи не изменяются и не удаляются. Он отвечает на вопрос «кто это сделал и когда», когда решение уже забыто.'
            }
            secondaryAction={
              hasFilters ? (
                <Button variant="outline" onClick={resetFilters}>
                  Сбросить фильтры
                </Button>
              ) : null
            }
            title={hasFilters ? 'Под фильтры ничего не подошло' : 'Действий пока нет'}
            description={
              hasFilters
                ? 'Измените период или снимите часть условий.'
                : 'Как только участники начнут добавлять и менять проектную информацию, здесь появится хронология.'
            }
          />
        ) : (
          <div className="max-w-3xl space-y-6">
            {grouped.map(([day, dayEntries]) => (
              <section key={day}>
                <h2 className="mb-2 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
                  {day}
                </h2>
                <ul className="divide-y rounded-md border">
                  {dayEntries.map((entry) => (
                    <ActivityRow key={entry.id} entry={entry} projectId={project.id} />
                  ))}
                </ul>
              </section>
            ))}

            {activity.hasNextPage ? (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={activity.isFetchingNextPage}
                  onClick={() => void activity.fetchNextPage()}
                >
                  {activity.isFetchingNextPage ? <Loader2Icon className="animate-spin" /> : null}
                  Показать ещё
                </Button>
              </div>
            ) : (
              <p className="text-center text-xs text-muted-foreground">Показана вся история</p>
            )}
          </div>
        )}
      </PageBody>
    </>
  );
}

function ActivityRow({ entry, projectId }: { entry: ActivityDto; projectId: string }) {
  const isItem = entry.entityType === 'ProjectItem' && entry.entityId;

  return (
    <li className="flex items-start gap-3 px-3 py-2.5">
      <UserAvatar user={entry.user} size="md" className="mt-0.5" />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-medium">{entry.user?.name ?? 'Система'}</span>
          <span className="text-xs text-muted-foreground">{entry.labelRu}</span>
        </div>

        {entry.entityTitle ? (
          <div className="mt-0.5 flex items-center gap-1.5">
            {entry.itemType ? <ItemTypeIcon type={entry.itemType} className="size-3.5" /> : null}
            {isItem ? (
              <Link
                to={`/projects/${projectId}/items?item=${entry.entityId}`}
                className="truncate text-sm hover:underline"
              >
                {entry.entityTitle}
              </Link>
            ) : (
              <span className="truncate text-sm">{entry.entityTitle}</span>
            )}
          </div>
        ) : null}

        {renderDetails(entry)}
      </div>

      <time
        className="shrink-0 text-2xs text-muted-foreground tabular-nums"
        dateTime={entry.createdAt}
        title={formatDateTime(entry.createdAt)}
      >
        {timeOfDay(entry.createdAt)}
      </time>
    </li>
  );
}

/** Подробности действия: что именно изменилось. */
function renderDetails(entry: ActivityDto): React.ReactNode {
  const metadata = entry.metadata;

  if (entry.action === 'ITEM_STATUS_CHANGED' && metadata.from && metadata.to) {
    return (
      <p className="mt-0.5 text-xs text-muted-foreground">
        {String(metadata.from)} → {String(metadata.to)}
      </p>
    );
  }

  if (Array.isArray(metadata.fields) && metadata.fields.length > 0) {
    return (
      <p className="mt-0.5 text-xs text-muted-foreground">
        {metadata.fields.map(String).join(', ')}
      </p>
    );
  }

  if (entry.action === 'RELATION_CREATED' || entry.action === 'RELATION_DELETED') {
    return (
      <p className="mt-0.5 text-xs text-muted-foreground">
        {String(metadata.fromTitle ?? '')} → {String(metadata.toTitle ?? '')}
      </p>
    );
  }

  if (entry.action === 'MEMBER_ROLE_CHANGED' && metadata.from && metadata.to) {
    return (
      <p className="mt-0.5 text-xs text-muted-foreground">
        {String(metadata.from)} → {String(metadata.to)}
      </p>
    );
  }

  return null;
}
